'use client'

import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@repo/ui/components'
import { cn } from '@repo/ui/lib/utils'
import { XIcon } from 'lucide-react'
import {
  type ComponentProps,
  createContext,
  type MouseEventHandler,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

type TagsContextType = {
  onOpenChange: (open: boolean) => void
  open: boolean
  setValue?: (value: string) => void
  setWidth?: (width: number) => void
  value?: string
  width?: number
}

const TagsContext = createContext<TagsContextType>({
  onOpenChange: () => {},
  open: false,
  setValue: undefined,
  setWidth: undefined,
  value: undefined,
  width: undefined,
})

const useTagsContext = () => {
  const context = useContext(TagsContext)

  if (!context) {
    throw new Error('useTagsContext must be used within a TagsProvider')
  }

  return context
}

export type TagsProps = {
  children?: ReactNode
  className?: string
  onOpenChange?: (open: boolean) => void
  open?: boolean
  setValue?: (value: string) => void
  value?: string
}

export const Tags = ({
  children,
  className,
  onOpenChange: controlledOnOpenChange,
  open: controlledOpen,
  setValue,
  value,
}: TagsProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const [width, setWidth] = useState<number>()
  const ref = useRef<HTMLDivElement>(null)

  const open = controlledOpen ?? uncontrolledOpen
  const onOpenChange = controlledOnOpenChange ?? setUncontrolledOpen

  useEffect(() => {
    if (!ref.current) {
      return
    }

    const resizeObserver = new ResizeObserver(entries => {
      setWidth(entries[0].contentRect.width)
    })

    resizeObserver.observe(ref.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <TagsContext.Provider value={{ onOpenChange, open, setValue, setWidth, value, width }} data-sentry-element="TagsContext.Provider" data-sentry-component="Tags" data-sentry-source-file="index.tsx">
      <Popover onOpenChange={onOpenChange} open={open} data-sentry-element="Popover" data-sentry-source-file="index.tsx">
        <div className={cn('relative w-full', className)} ref={ref}>
          {children}
        </div>
      </Popover>
    </TagsContext.Provider>
  )
}

export type TagsTriggerProps = ComponentProps<typeof Button>

export const TagsTrigger = ({ children, className, ...props }: TagsTriggerProps) => (
  <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-component="TagsTrigger" data-sentry-source-file="index.tsx">
    <Button
      className={cn('h-auto w-full justify-between p-2', className)}
      // biome-ignore lint/a11y/useSemanticElements: "Required"
      role="combobox"
      variant="outline"
      {...props}
    data-sentry-element="Button" data-sentry-source-file="index.tsx">
      <div className="flex flex-wrap items-center gap-1">{children}</div>
    </Button>
  </PopoverTrigger>
)

export type TagsValueProps = ComponentProps<typeof Badge>

export const TagsValue = ({
  children,
  className,
  onRemove,
  ...props
}: TagsValueProps & { onRemove?: () => void }) => {
  const handleRemove: MouseEventHandler<HTMLDivElement> = event => {
    event.preventDefault()
    event.stopPropagation()
    onRemove?.()
  }

  return (
    <Badge className={cn('flex items-center gap-2', className)} {...props} data-sentry-element="Badge" data-sentry-component="TagsValue" data-sentry-source-file="index.tsx">
      {children}
      {/* A pointer shortcut inside the trigger button, which a nested button can't be; keyboard
          and screen reader users remove a tag by toggling it in the list. */}
      {onRemove && (
        <div
          aria-hidden="true"
          className="size-auto hover:text-muted-foreground"
          onClick={handleRemove}
        >
          <XIcon size={12} />
        </div>
      )}
    </Badge>
  )
}

export type TagsContentProps = ComponentProps<typeof PopoverContent> & {
  shouldFilter?: boolean
}

export const TagsContent = ({ children, className, shouldFilter, ...props }: TagsContentProps) => {
  const { width } = useTagsContext()

  return (
    <PopoverContent className={cn('p-0', className)} style={{ width }} {...props} data-sentry-element="PopoverContent" data-sentry-component="TagsContent" data-sentry-source-file="index.tsx">
      <Command shouldFilter={shouldFilter} data-sentry-element="Command" data-sentry-source-file="index.tsx">{children}</Command>
    </PopoverContent>
  )
}

export type TagsInputProps = ComponentProps<typeof CommandInput>

export const TagsInput = ({ className, ...props }: TagsInputProps) => (
  <CommandInput className={cn('h-9', className)} {...props} data-sentry-element="CommandInput" data-sentry-component="TagsInput" data-sentry-source-file="index.tsx" />
)

export type TagsListProps = ComponentProps<typeof CommandList>

export const TagsList = ({ className, ...props }: TagsListProps) => (
  <CommandList className={cn('max-h-[200px]', className)} {...props} data-sentry-element="CommandList" data-sentry-component="TagsList" data-sentry-source-file="index.tsx" />
)

export type TagsEmptyProps = ComponentProps<typeof CommandEmpty>

export const TagsEmpty = ({ children, className, ...props }: TagsEmptyProps) => (
  <CommandEmpty {...props} data-sentry-element="CommandEmpty" data-sentry-component="TagsEmpty" data-sentry-source-file="index.tsx">{children ?? 'No tags found.'}</CommandEmpty>
)

export type TagsGroupProps = ComponentProps<typeof CommandGroup>

export const TagsGroup = CommandGroup

export type TagsItemProps = ComponentProps<typeof CommandItem>

export const TagsItem = ({ className, ...props }: TagsItemProps) => (
  <CommandItem className={cn('items-center justify-between', className)} {...props} data-sentry-element="CommandItem" data-sentry-component="TagsItem" data-sentry-source-file="index.tsx" />
)
